import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../../components/Auth/AuthLayout';
import FormField from '../../../components/Auth/FormField';
import LoadingButton from '../../../components/Auth/LoadingButton';
import { ProfileImageUpload } from '../../../components/FileUpload';

const AirlinerCompleteProfilePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    businessType: '',
    registrationNumber: '',
    taxId: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    phone: '',
    website: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState(null);

  const businessTypes = [
    { value: '', label: 'Select Business Type' },
    { value: 'airline', label: 'Commercial Airline' },
    { value: 'charter', label: 'Charter Service' },
    { value: 'cargo', label: 'Cargo Airline' },
    { value: 'private', label: 'Private Aviation' },
    { value: 'maintenance', label: 'Aircraft Maintenance' },
    { value: 'other', label: 'Other' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLogoSelect = async (file) => {
    setUploadingLogo(true);
    setLogoUploadError(null);

    try {
      // For now, just store the file locally since we don't have a company ID yet
      const previewUrl = URL.createObjectURL(file);
      setCompanyLogo({
        file,
        preview: previewUrl,
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      // console.log('Company logo selected:', file.name);
    } catch (err) {
      setLogoUploadError(err.message || 'Failed to select logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoRemove = () => {
    if (companyLogo?.preview) {
      URL.revokeObjectURL(companyLogo.preview);
    }
    setCompanyLogo(null);
    setLogoUploadError(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData.businessType) {
      newErrors.businessType = 'Please select a business type';
    }

    if (!formData.registrationNumber.trim()) {
      newErrors.registrationNumber = 'Registration number is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate success
      alert('Profile completed successfully!');
      navigate('/airliner/auth/registration-success');
    } catch (error) {
      setErrors({ submit: 'Failed to complete profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const profileIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
  );

  return (
    <AuthLayout
      title="Complete Your Profile"
      subtitle="Please provide your company details to complete registration"
      icon={profileIcon}
      iconBgColor="bg-jet-yellow-100"
      iconColor="text-jet-yellow-600"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-jet-gray-900">Company Information</h3>
          
          <FormField
            label="Company Name"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            placeholder="Enter your company name"
            required
            error={errors.companyName}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Business Type <span className="text-jet-red-500">*</span>
              </label>
              <select
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none transition-colors ${
                  errors.businessType 
                    ? 'border-jet-red-400 focus:border-jet-red-500' 
                    : 'border-jet-gray-300 focus:border-jet-yellow'
                }`}
                required
              >
                {businessTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {errors.businessType && (
                <p className="mt-1 text-sm text-jet-red-600">{errors.businessType}</p>
              )}
            </div>

            <FormField
              label="Registration Number"
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleChange}
              placeholder="Business registration number"
              required
              error={errors.registrationNumber}
            />
          </div>

          <FormField
            label="Tax ID (Optional)"
            name="taxId"
            value={formData.taxId}
            onChange={handleChange}
            placeholder="Tax identification number"
            error={errors.taxId}
          />
        </div>

        {/* Company Logo */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-jet-gray-900">Company Logo</h3>
          <ProfileImageUpload
            onImageSelect={handleLogoSelect}
            onImageRemove={handleLogoRemove}
            currentImage={companyLogo?.preview}
            maxSize={2 * 1024 * 1024} // 2MB
            loading={uploadingLogo}
            error={logoUploadError}
            label="Upload Company Logo"
            shape="square"
            size="medium"
            required={false}
          />
          
          {logoUploadError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{logoUploadError}</p>
            </div>
          )}
        </div>

        {/* Address Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-jet-gray-900">Address Information</h3>
          
          <FormField
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Street address"
            required
            error={errors.address}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City"
              required
              error={errors.city}
            />

            <FormField
              label="State/Province"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="State or Province"
              error={errors.state}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              placeholder="Country"
              required
              error={errors.country}
            />

            <FormField
              label="ZIP/Postal Code"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              placeholder="ZIP or Postal Code"
              error={errors.zipCode}
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-jet-gray-900">Contact Information</h3>
          
          <FormField
            label="Phone Number"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Business phone number"
            required
            error={errors.phone}
          />

          <FormField
            label="Website (Optional)"
            name="website"
            type="url"
            value={formData.website}
            onChange={handleChange}
            placeholder="https://www.yourcompany.com"
            error={errors.website}
          />

          <div>
            <label className="block text-sm font-medium text-jet-gray-700 mb-2">
              Company Description (Optional)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow transition-colors"
              placeholder="Brief description of your company and services"
            />
          </div>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-jet-red-50 border border-jet-red-200 rounded-md p-3">
            <p className="text-sm text-jet-red-600">{errors.submit}</p>
          </div>
        )}

        {/* Submit Button */}
        <LoadingButton
          type="submit"
          loading={loading}
          loadingText="Completing profile..."
          className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900"
        >
          Complete Profile
        </LoadingButton>

        {/* Back to Login */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <button
            type="button"
            onClick={() => navigate('/airliner/auth/login')}
            className="text-jet-gray-600 hover:text-jet-gray-900 text-sm transition-colors"
          >
            Back to Login
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AirlinerCompleteProfilePage;